import { useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  DetectedWallet,
  useWalletSign,
} from '../../xpx-perps-withdrawal/useWalletSign';
import {
  TransferItem,
  approveTransfer,
} from '../../../services/xbitRelayTransfersService';
import { buildApproveMessage } from './MessageBuilders';

type Props = {
  record: TransferItem;
  onClose(): void;
};

const isUserRejection = (err: any): boolean => {
  const code = err?.code ?? err?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED') return true;
  const msg = (err?.message || '').toLowerCase();
  return msg.includes('reject') || msg.includes('denied');
};

export function ApproveDialog({ record, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { detectedWallets, connect, address, signMessage } = useWalletSign();
  const [selectedWallet, setSelectedWallet] = useState<DetectedWallet | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!selectedWallet) return;
    setConnecting(true);
    try {
      await connect(selectedWallet.provider);
    } catch (err: any) {
      if (!isUserRejection(err)) {
        notify(err?.message || t('common.error'), { type: 'error' });
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleSignAndApprove = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const timestampMs = Date.now();
      const message = buildApproveMessage(record.id, timestampMs);
      const signature = await signMessage(message);
      await approveTransfer(record.id, address.toLowerCase(), signature, timestampMs);
      notify(t('module.xbitRelayTransfers.approveSuccess'), { type: 'success' });
      refresh();
      onClose();
    } catch (err: any) {
      if (isUserRejection(err)) return;
      notify(
        err?.response?.data?.message || err?.message || t('common.error'),
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayTransfers.approveTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <b>{t('module.xbitRelayTransfers.userId')}:</b> {record.userId}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayTransfers.provider')}:</b> {record.fromProvider}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayTransfers.chainId')}:</b> {record.chainId}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              <b>{t('module.xbitRelayTransfers.toAddress')}:</b> {record.toAddress}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayTransfers.amount')}:</b> {record.amount}{' '}
              {record.tokenSymbol || record.token}
            </Typography>
          </Stack>

          {detectedWallets.length === 0 ? (
            <Typography variant="body2" color="error">
              {t('module.xbitRelayTransfers.noWalletFound')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('module.xbitRelayTransfers.selectWallet')}</InputLabel>
              <Select
                label={t('module.xbitRelayTransfers.selectWallet')}
                value={selectedWallet?.name ?? ''}
                onChange={(e) => {
                  const w = detectedWallets.find((x) => x.name === e.target.value);
                  setSelectedWallet(w ?? null);
                }}
                disabled={Boolean(address) || connecting || loading}
              >
                {detectedWallets.map((w) => (
                  <MenuItem key={w.name} value={w.name}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!address && selectedWallet && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleConnect}
              disabled={connecting}
              startIcon={connecting ? <CircularProgress size={14} /> : null}
            >
              {t('module.xbitRelayTransfers.connect')}
            </Button>
          )}

          {address && (
            <Typography variant="caption" color="text.secondary">
              {t('module.xbitRelayTransfers.connectedAs')}: {address}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading || connecting}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSignAndApprove}
          disabled={!address || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayTransfers.signAndApprove')}
        </Button>
      </DialogActions>
    </>
  );
}
